import { Box, Typography } from "@mui/material";
import { FC } from "react";
import { useForm } from "react-hook-form";
import InputOtp from "../../../components/InputOtp";
import Button from "../../../components/Button";
import { ReactComponent as ErrorIcon } from "../../../assets/Icons/InfoFill.svg";
import useVerifyOTPMutation from "../hooks/useVerifyOTPMutation";
import useRecoverPasswordMutation from "../hooks/useRecoverPasswordMutation";
import extractErrorMessage from "../../../helpers/extractErrorMessage";
import { useTranslation } from "react-i18next";

interface VerifyOtpFormProps {
  email?: string;
  onSuccess?: () => void;
}

const VerifyOtpForm: FC<VerifyOtpFormProps> = ({ onSuccess, email }) => {
  const { t } = useTranslation();

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: {
      otp: "",
    },
    mode: "onChange",
  });

  const { mutate: recoverPasswordMutate, isPending: isSendingRequest } =
    useRecoverPasswordMutation(() => {
      reset();
      resetVerifyOtpState();
    });

  const {
    mutate: verifyOtpMutate,
    isPending: isVerifingOtp,
    error: verifyOtpError,
    reset: resetVerifyOtpState,
  } = useVerifyOTPMutation(onSuccess);

  const isOtpExpired = extractErrorMessage(verifyOtpError)?.includes("expired");

  const isSubmitDisable =
    watch("otp")?.length < 4 || isVerifingOtp || isSendingRequest;

  return (
    <>
      <Typography fontWeight={700} variant="h5">
        {t("Enter_One_Time_Password")}
      </Typography>
      <Typography fontSize={16} my={2}>
        {t("Enter_Otp_Text")}
      </Typography>
      <Typography mb={3}>{t("Otp_Note")}</Typography>

      {verifyOtpError && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          mb={2}
          gap={1}
        >
          <ErrorIcon width="16px" height="16px" />
          <Typography fontSize={12} color="custom.foreground.attention">
            {isOtpExpired ? t("Expired_OTP_error") : t("Invalid_OTP_error")}
          </Typography>
        </Box>
      )}

      <InputOtp control={control} name="otp" autoFocus />

      <Button
        onClick={handleSubmit((val) => {
          verifyOtpMutate({
            email,
            otp: val?.otp,
          });
        })}
        type="submit"
        variant="contained"
        fullWidth
        disabled={isSubmitDisable}
        sx={{
          mt: 3,
          backgroundColor: "custom.background.brand",
          textTransform: "none",
          fontSize: 16,
        }}
      >
        {t("Submit")}
      </Button>

      <Typography
        fontSize="18px"
        color="custom.foreground.secondary"
        mt={4}
        textAlign="center"
        onClick={() => {
          email && recoverPasswordMutate({ email });
        }}
        sx={{
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {t("Resend_One_Time_Password")}
      </Typography>
    </>
  );
};

export default VerifyOtpForm;
